/**
 * AssemblyAI Supported Languages
 * Based on AssemblyAI documentation for automatic language detection
 * and manual language specification
 */

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  tier: 'best' | 'standard';
}

/**
 * List of languages supported by AssemblyAI
 * These can be used for both automatic detection and manual specification
 */
export const ASSEMBLYAI_LANGUAGES: Language[] = [
  // Best Tier Languages (17 languages with highest accuracy)
  { code: 'en', name: 'English', nativeName: 'English', tier: 'best' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', tier: 'best' },
  { code: 'fr', name: 'French', nativeName: 'Français', tier: 'best' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', tier: 'best' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', tier: 'best' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', tier: 'best' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', tier: 'best' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', tier: 'best' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', tier: 'best' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', tier: 'best' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', tier: 'best' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', tier: 'best' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', tier: 'best' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', tier: 'best' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', tier: 'best' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', tier: 'best' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', tier: 'best' },
  
  // Additional Standard Tier Languages
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', tier: 'standard' },
  { code: 'ca', name: 'Catalan', nativeName: 'Català', tier: 'standard' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', tier: 'standard' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', tier: 'standard' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', tier: 'standard' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', tier: 'standard' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', tier: 'standard' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', tier: 'standard' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', tier: 'standard' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', tier: 'standard' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', tier: 'standard' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', tier: 'standard' },
];

/**
 * Language codes mapped to their display names
 */
export const LANGUAGE_NAMES: Record<string, string> = ASSEMBLYAI_LANGUAGES.reduce(
  (acc, lang) => ({
    ...acc,
    [lang.code]: lang.name,
  }),
  {} as Record<string, string>
);

/**
 * Get display name for a language code
 */
export function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code] || code.toUpperCase();
}

/**
 * Options for language selection dropdown
 */
export const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect language' },
  ...ASSEMBLYAI_LANGUAGES.map(lang => ({
    value: lang.code,
    label: `${lang.name} (${lang.nativeName})`,
    tier: lang.tier,
  })),
];

/**
 * Get the appropriate AssemblyAI configuration based on language selection
 */
export function getAssemblyAILanguageConfig(languageCode: string | null) {
  if (!languageCode || languageCode === 'auto') {
    // Use automatic language detection
    return { language_detection: true };
  } else {
    // Use specific language code
    return { language_code: languageCode };
  }
}

/**
 * Check if a language code is supported
 */
export function isSupportedLanguage(code: string): boolean {
  return ASSEMBLYAI_LANGUAGES.some(lang => lang.code === code);
}

/**
 * Get language by code
 */
export function getLanguageByCode(code: string): Language | undefined {
  return ASSEMBLYAI_LANGUAGES.find(lang => lang.code === code);
}